import type { ReactElement } from 'react';

import { Checkbox } from 'components/Checkbox';
import { Tooltip } from 'components/Tooltip';
import type { Contact } from 'domain/contact';
import { Crm } from 'domain/crm';
import { useContactsAvailabilityStatus } from 'features/contacts/hooks/useContactsAvailabilityStatus';
import { useIsDataCreditsEnabled } from 'features/quota/hooks/useIsDataCreditsEnabled';
import { useQuota } from 'features/quota/hooks/useQuota';
import { useUserQuery } from 'features/user/useUserQuery';
import { useCrmStatus } from 'hooks/useCrmStatus';
import { useIsSwUser } from 'hooks/useIsSwUser';
import { useUserStatus } from 'hooks/useUserStatus';
import { StyledBulkRevealButton } from 'pages/contacts/BulkActionBar/BulkRevealButton/styles';
import { StyledCrmExportBulkButton } from 'pages/contacts/ContactItem/CrmExportContainer/styles';
import { GdprRestrictedButton } from 'pages/contacts/ContactItem/GdprRestrictedButton';
import { UpgradeButton } from 'pages/contacts/ContactItem/UpgradeButton/UpgradeButton';
import { appStore } from 'services/appStore';
import { translate } from 'services/i18n';

import { useBulkSelectionState } from '../hooks/useBulkSelectionState';

import { BulkCrmExportActionsContainer } from './BulkCrmExportActionsContainer';
import { BulkRevealButton } from './BulkRevealButton';
import { BulkRevealDropdown } from './BulkRevealButton/BulkRevealDropdown';
import { RevealQuotasTooltipContent } from './RevealTooltipContent/RevealTooltipContent';
import { StyledBulkActionBarContainer, StyledSelectedText, StyledSeparator } from './styles';

const MAX_CONTACTS_FOR_BULK_ACTION = 100;

export const BulkActionBar = ({
  contacts,
  setShouldAnimatePrivateDataMount,
}: {
  contacts: Partial<Contact>[];
  setShouldAnimatePrivateDataMount: (shouldAnimate: boolean) => void;
}) => {
  const { isGdprRestricted } = useContactsAvailabilityStatus();
  const { isTrialOrAfterTrial, isNoTouch } = useUserStatus();
  const { data: userData } = useUserQuery();
  const { isSfExportEnabled } = useCrmStatus();
  const { isDataCreditsEnabled } = useIsDataCreditsEnabled();

  const {
    contactsAvailableForReveal,
    selectedContactsAvailableForReveal,
    contactsToRevealCount,
    areNoContactsSelected,
    areAllSelected,
  } = useBulkSelectionState(contacts);

  const { quota, canAfford, getRevealCost } = useQuota();

  const isSwUser = useIsSwUser();

  const handleCheckboxClick = () => {
    if (areAllSelected) {
      appStore.setSelectedContacts([]);
    } else {
      appStore.setSelectedContacts(contactsAvailableForReveal as Contact[], true);
    }
  };

  const renderButton = (isExport: boolean) => {
    const revealCost = getRevealCost(contactsToRevealCount);
    const isRevealAffordable = canAfford(revealCost);

    const isExportDisabled = areNoContactsSelected || !isRevealAffordable;

    const isExportUpgradedButtonEnabled = isExport && (isTrialOrAfterTrial || isNoTouch);
    // For data credits mode, skip upgrade button for reveal - dropdown will show credits info
    const isRevealUpgradeButtonEnabled =
      !isExport && !isDataCreditsEnabled && (isTrialOrAfterTrial || isNoTouch) && quota.isDepleted;

    if (isGdprRestricted) {
      return <GdprRestrictedButton placement="top" variant={isExport ? 'bulk-export' : 'bulk-reveal'} />;
    }
    if (isExportUpgradedButtonEnabled) {
      return <UpgradeButton variant={'bulk-export'} />;
    }

    if (isRevealUpgradeButtonEnabled) {
      return <UpgradeButton variant="bulk-reveal" />;
    }

    // For data credits mode on reveal, skip not enough credits tooltip - dropdown shows credits info
    if (!isRevealAffordable && (isExport || !isDataCreditsEnabled)) {
      let tooltipContent: ReactElement | string = translate('contact_card.reveal.no_credits');
      const accountAdminEmails = userData?.accountAdminEmails ?? [];

      if (!isSwUser && accountAdminEmails.length > 0) {
        tooltipContent += ` - ${accountAdminEmails.join(', ')}`;
      }

      if (isTrialOrAfterTrial) {
        tooltipContent = (
          <RevealQuotasTooltipContent
            title={translate('si.contacts.reveal.tooltip.title')}
            description={translate('si.contacts.reveal.tooltip.description')}
          />
        );
      }

      return (
        <Tooltip content={tooltipContent} placement="top" minWidth={230}>
          {isExport ? (
            <StyledCrmExportBulkButton
              data-automation="CrmExportBulkButton"
              iconName={isSfExportEnabled ? Crm.salesforce : Crm.hubspot}
              iconSize="xs"
              variant="flat"
              disabled
            >
              {translate('contact_card.export_crm')}
            </StyledCrmExportBulkButton>
          ) : (
            <StyledBulkRevealButton variant="flat" data-automation="ContactReveal" disabled>
              {translate('contact_card.reveal')}
            </StyledBulkRevealButton>
          )}
        </Tooltip>
      );
    }

    if (isExport && selectedContactsAvailableForReveal.length > MAX_CONTACTS_FOR_BULK_ACTION) {
      return (
        <Tooltip
          content={translate('contacts.bulk.export_to_crm.tooltip.limit', { limit: MAX_CONTACTS_FOR_BULK_ACTION })}
          placement="top"
          minWidth={230}
        >
          <StyledCrmExportBulkButton
            data-automation="CrmExportBulkButton"
            iconName={isSfExportEnabled ? Crm.salesforce : Crm.hubspot}
            iconSize="xs"
            variant="flat"
            disabled
          >
            {translate('contact_card.export_crm')}
          </StyledCrmExportBulkButton>
        </Tooltip>
      );
    }

    if (isExport) {
      return (
        <BulkCrmExportActionsContainer
          isDisabled={isExportDisabled}
          setShouldAnimatePrivateDataMount={setShouldAnimatePrivateDataMount}
        />
      );
    }

    if (isDataCreditsEnabled) {
      return (
        <BulkRevealDropdown
          isDisabled={areNoContactsSelected}
          contactsToRevealCount={contactsToRevealCount}
          onRevealFinish={() => setShouldAnimatePrivateDataMount(true)}
        />
      );
    }

    return (
      <BulkRevealButton
        isDisabled={areNoContactsSelected || quota.isDepleted}
        onRevealFinish={() => setShouldAnimatePrivateDataMount(true)}
      />
    );
  };

  const isCheckboxDisabled = (): boolean => {
    if (isGdprRestricted) {
      return true;
    }

    if (isDataCreditsEnabled) {
      return false;
    }

    return (isTrialOrAfterTrial || isNoTouch) && quota.isDepleted;
  };

  return (
    <StyledBulkActionBarContainer data-automation="BulkActionBar">
      <Checkbox
        isChecked={areAllSelected}
        isHalfChecked={
          selectedContactsAvailableForReveal.length > 0
            ? selectedContactsAvailableForReveal.length < contactsAvailableForReveal.length
            : null
        }
        disabled={isCheckboxDisabled()}
        onClick={handleCheckboxClick}
        dataAutomation="BulkActionBarCheckbox"
      />
      <StyledSelectedText>{`${selectedContactsAvailableForReveal.length} ${translate(
        'common.selected'
      )}`}</StyledSelectedText>
      <StyledSeparator />
      {renderButton(false)}
      {renderButton(true)}
    </StyledBulkActionBarContainer>
  );
};
